// The **practice capability gate** (plan **D4**, **D18**; design **D-I**).
//
// Practice simulates the whole season on the player's own device, so a device that cannot do it is
// **refused honestly**: the gate names *which* check failed. "Your browser can't" is unfalsifiable
// and unreportable, and naming the failed check is also what makes **D18**'s measured-device policy
// visible to the person it affects.
//
// ## Why this is a seam rather than a direct read of `navigator`
//
// Two of the four checks want origin-private file storage and a quota estimate, and the client test
// suite has no OPFS harness yet (`tasks.md` 1.6, not this task's). Expressing the gate as an
// injected probe means the *refusal surface* and the composition root's gating are testable now,
// against a fixed probe, while the real browser probe below is exercised through injected `navigator`
// facets today and through 1.6's harness once it exists. The seam is also what `client-app` requires
// of the root — "a capability gate evaluated before any practice affordance is offered" is a thing
// the root owns and no screen constructs.
//
// ## Why an unanswerable quota is not a refusal
//
// Quota is an **estimate, not a reservation** (architecture review **F6**, adopted). A browser that
// declines to estimate is not a browser that cannot hold a run, and refusing it would trade a real
// capability for a guess. Mid-run exhaustion is therefore handled as a first-class failure
// elsewhere (`rounds-local-run`: evict oldest, retry once, then fail typed) rather than being
// pre-empted here.

/** The four checks, in the order the refusal checklist reads them. */
export const PRACTICE_CAPABILITIES = [
  'webassembly',
  'secureContext',
  'privateFileStorage',
  'freeSpace',
] as const;

export type PracticeCapabilityName = (typeof PRACTICE_CAPABILITIES)[number];

export interface PracticeCapabilityCheck {
  name: PracticeCapabilityName;
  ok: boolean;
}

/**
 * The gate's answer. It carries the **passing** checks as well as the failing ones on purpose: the
 * refusal surface shows what the browser *does* offer, which reads as a limitation rather than an
 * accusation, and gives the player something concrete to report.
 */
export interface PracticeCapabilityReport {
  supported: boolean;
  checks: readonly PracticeCapabilityCheck[];
  /** Every failed check, in `PRACTICE_CAPABILITIES` order. Empty iff `supported`. */
  missing: readonly PracticeCapabilityName[];
}

/** The gate seam the composition root owns. */
export interface PracticeCapabilityProbe {
  evaluate(): Promise<PracticeCapabilityReport>;
}

/** One capability's player-facing name and the reason practice needs it. Copy lives beside the
 * vocabulary so a new check cannot be added without a sentence explaining it. */
export interface PracticeCapabilityCopy {
  label: string;
  need: string;
}

const COPY: Record<PracticeCapabilityName, PracticeCapabilityCopy> = {
  webassembly: {
    label: 'Runs WebAssembly',
    need: 'the simulation itself runs as WebAssembly on this device',
  },
  secureContext: {
    label: 'Secure connection',
    need: 'private file storage is only offered over a secure connection',
  },
  privateFileStorage: {
    label: 'Private file storage',
    need: 'needed to hold the season while you scrub back through it',
  },
  freeSpace: {
    label: 'Enough free space',
    need: 'a full season is a few hundred megabytes while you are watching it',
  },
};

export function practiceCapabilityCopy(name: PracticeCapabilityName): PracticeCapabilityCopy {
  return COPY[name];
}

/** Builds a report from the four answers, so `supported`/`missing`/`checks` can never disagree. */
export function practiceCapabilityReport(answers: {
  webassembly: boolean;
  secureContext: boolean;
  privateFileStorage: boolean;
  freeSpace: boolean;
}): PracticeCapabilityReport {
  const checks = PRACTICE_CAPABILITIES.map((name) => ({ name, ok: answers[name] }));
  const missing = checks.filter((check) => !check.ok).map((check) => check.name);
  return { supported: missing.length === 0, checks, missing };
}

/**
 * The bytes one practice run is expected to want, as the free-space floor.
 *
 * Measured: a solo run projects to ~229 MB and a 7-colony run to ~586 MB
 * (`docs/dev/evidence/wasm-sim-spike/`). The floor is the larger of those plus headroom, because the
 * gate runs before the player has chosen a rival count and must not admit a device that can only
 * hold the cheapest configuration.
 */
export const PRACTICE_MIN_FREE_BYTES = 1024 ** 3;

/** The facets of the browser the probe reads. Narrowed to exactly this so the probe can be driven
 * with a plain object and never needs a global patched underneath it. */
export interface PracticeEnvironmentFacets {
  /** `globalThis.WebAssembly`, or `undefined`. */
  webassembly: unknown;
  /** `window.isSecureContext`. */
  isSecureContext: boolean;
  /** `navigator.storage`, or `undefined` — which is what a plain-HTTP origin reports. */
  storage:
    | {
        getDirectory?: () => Promise<unknown>;
        estimate?: () => Promise<{ quota?: number; usage?: number }>;
      }
    | undefined;
}

/** Reads the real browser. Every access is guarded: a probe that throws would refuse a capable
 * device for the wrong reason. */
export function browserEnvironmentFacets(): PracticeEnvironmentFacets {
  let storage: PracticeEnvironmentFacets['storage'];
  try {
    storage = globalThis.navigator?.storage as PracticeEnvironmentFacets['storage'];
  } catch {
    storage = undefined;
  }
  let secure = false;
  try {
    secure = globalThis.isSecureContext === true;
  } catch {
    secure = false;
  }
  return {
    webassembly: (globalThis as { WebAssembly?: unknown }).WebAssembly,
    isSecureContext: secure,
    storage,
  };
}

export function browserPracticeCapabilityProbe(
  facets: PracticeEnvironmentFacets = browserEnvironmentFacets(),
): PracticeCapabilityProbe {
  return {
    async evaluate(): Promise<PracticeCapabilityReport> {
      const webassembly = facets.webassembly !== undefined && facets.webassembly !== null;
      const secureContext = facets.isSecureContext === true;
      const privateFileStorage = typeof facets.storage?.getDirectory === 'function';

      // Unknown free space is **not** a failure — see the module header.
      let freeSpace = true;
      if (typeof facets.storage?.estimate === 'function') {
        try {
          const estimate = await facets.storage.estimate();
          const quota = estimate.quota;
          if (typeof quota === 'number' && Number.isFinite(quota)) {
            const usage = typeof estimate.usage === 'number' ? estimate.usage : 0;
            freeSpace = quota - usage >= PRACTICE_MIN_FREE_BYTES;
          }
        } catch {
          freeSpace = true;
        }
      }

      return practiceCapabilityReport({
        webassembly,
        secureContext,
        privateFileStorage,
        freeSpace,
      });
    },
  };
}

/** A probe that refuses exactly the named checks and passes the rest. The test double for every
 * surface that has to render a refusal without an OPFS harness. */
export function fixedPracticeCapabilityProbe(
  ...missing: readonly PracticeCapabilityName[]
): PracticeCapabilityProbe {
  const failed = new Set(missing);
  return {
    evaluate(): Promise<PracticeCapabilityReport> {
      return Promise.resolve(
        practiceCapabilityReport({
          webassembly: !failed.has('webassembly'),
          secureContext: !failed.has('secureContext'),
          privateFileStorage: !failed.has('privateFileStorage'),
          freeSpace: !failed.has('freeSpace'),
        }),
      );
    },
  };
}
